import React, { useEffect, useState } from "react";
import { Modal, Input, Spin } from "antd";
import { svg } from "../../../Assets/svg";
import { useAppDispatch } from "../../../store";
import { getDecryptedUserData, decryptByText } from "../../../helpers/bycrypt";
import { ROLE } from "../../../helpers/utils";
import {
  getBusinessTimeZone,
  formatDateWithBusinessTZ,
  formatCalendarDateDisplay,
  getEmploymentEndDateString,
} from "../../../helpers/moment";
import { getDayNoteAPI, saveDayNoteAPI } from "./dayNotesApis";
import { BtnLoader } from "../../../Components/Components";
import moment from "moment-timezone";

const { TextArea } = Input;

interface DayNotesDetailModalProps {
  visible: boolean;
  onClose: () => void;
  userId?: string;
  employeeName?: string;
  showEmployeeName?: boolean;
  date?: string; // Format: YYYY-MM-DD
  isPayrollApproved?: boolean;
  onSuccess?: (savedData?: any) => void;
}

export const DayNotesDetailModal: React.FC<DayNotesDetailModalProps> = ({
  visible,
  onClose,
  userId,
  employeeName,
  showEmployeeName,
  date,
  isPayrollApproved,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const userData = getDecryptedUserData();
  const userRole = userData?.role?.key || userData?.role;
  const userRolesList = Array.isArray(userData?.roles)
    ? userData.roles.map((r: any) => (typeof r === "object" ? r?.key : r))
    : [];

  const adminRoles = [
    ROLE.ADMIN,
    ROLE.SUB_ADMIN,
    (ROLE as any).HR,
    (ROLE as any).HR_MANAGER,
  ];

  const isAdminOrManager =
    adminRoles.includes(userRole) ||
    userRolesList.some((r: string) => adminRoles.includes(r)) ||
    userData?.isEmployee === false;

  const tz = getBusinessTimeZone();
  const todayDateStr = formatDateWithBusinessTZ(new Date(), "YYYY-MM-DD");
  const effectiveDateStr = date || todayDateStr;

  const [employeeNotes, setEmployeeNotes] = useState("");
  const [managerNotes, setManagerNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [adminNoteAudit, setAdminNoteAudit] = useState<{
    name: string;
    time: string;
  } | null>(null);
  const [fetchedEmployeeName, setFetchedEmployeeName] = useState<string>("");
  const [isPastEndDate, setIsPastEndDate] = useState<boolean>(false);
  const [empEndDate, setEmpEndDate] = useState<string | Date | null>(null);

  const formattedDisplayDate = moment(effectiveDateStr, "YYYY-MM-DD").isValid()
    ? moment.tz(effectiveDateStr, "YYYY-MM-DD", tz).format("DD-MM-YYYY")
    : formatDateWithBusinessTZ(new Date(), "DD-MM-YYYY");

  const fetchNote = () => {
    setLoading(true);
    dispatch(
      getDayNoteAPI({
        date: effectiveDateStr,
        user: userId,
        onSuccess: (data) => {
          setEmployeeNotes(data?.employee_notes || "");
          setManagerNotes(data?.manager_notes || "");
          setIsPastEndDate(Boolean(data?.is_past_end_date));
          setEmpEndDate(data?.employment_end_date || null);

          if (data?.user && typeof data.user === "object") {
            const fName = data.user.first_name
              ? decryptByText(data.user.first_name)
              : "";
            const lName = data.user.last_name
              ? decryptByText(data.user.last_name)
              : "";
            const fullName = `${fName} ${lName}`.trim() || data.user.name || "";
            if (fullName) {
              setFetchedEmployeeName(fullName);
            }
          }

          const updatedByObj = data?.updated_by || data?.created_by;
          const hasManagerNote = Boolean(
            data?.manager_notes && data.manager_notes.trim() !== "",
          );
          if (hasManagerNote) {
            let updaterName = "Admin";
            if (updatedByObj && typeof updatedByObj === "object") {
              const uF = updatedByObj.first_name
                ? decryptByText(updatedByObj.first_name)
                : "";
              const uL = updatedByObj.last_name
                ? decryptByText(updatedByObj.last_name)
                : "";
              const fullName = `${uF} ${uL}`.trim();
              if (fullName) {
                updaterName = fullName;
              } else if (updatedByObj.name) {
                updaterName = updatedByObj.name;
              }
            }
            const updateTime = data?.updated_at || data?.created_at;
            const formattedTime = updateTime
              ? formatDateWithBusinessTZ(updateTime, "DD-MM-YYYY, hh:mm A")
              : "";
            setAdminNoteAudit({ name: updaterName, time: formattedTime });
          } else {
            setAdminNoteAudit(null);
          }

          setLoading(false);
        },
        onError: () => {
          setLoading(false);
        },
      }) as any,
    );
  };

  useEffect(() => {
    if (visible) {
      fetchNote();
    } else {
      setEmployeeNotes("");
      setManagerNotes("");
      setAdminNoteAudit(null);
      setFetchedEmployeeName("");
      setIsPastEndDate(false);
      setEmpEndDate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, userId, effectiveDateStr]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isPastEndDate) return;
    setSubmitting(true);

    const payload: any = {
      date: effectiveDateStr,
      employee_notes: employeeNotes,
    };

    if (userId) {
      payload.user = userId;
    }

    if (isAdminOrManager) {
      payload.manager_notes = managerNotes;
    }

    dispatch(
      saveDayNoteAPI({
        payload,
        onSuccess: (savedData: any) => {
          setSubmitting(false);
          onSuccess?.(savedData);
          onClose();
        },
        onError: () => {
          setSubmitting(false);
        },
      }) as any,
    );
  };

  const hasAdminNotes = Boolean(managerNotes && managerNotes.trim() !== "");
  const isEmployeeNotesEditable =
    !isPastEndDate && (isAdminOrManager || (!isPayrollApproved && !hasAdminNotes));
  const isAdminNotesEditable = !isPastEndDate && isAdminOrManager;
  const canSubmit = !isPastEndDate && (isAdminOrManager || isEmployeeNotesEditable);
  const displayEmployeeName = employeeName || fetchedEmployeeName;

  const formattedEmpEndDate = React.useMemo(() => {
    const str = getEmploymentEndDateString(empEndDate);
    return str
      ? moment.utc(str, "YYYY-MM-DD").format("DD/MM/YYYY")
      : formatCalendarDateDisplay(empEndDate, "DD/MM/YYYY");
  }, [empEndDate]);

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      closeIcon={svg?.closeIcon}
      centered
      destroyOnClose
      className="day-notes-detail-modal"
    >
      <div className="modal-content border-0 p-0 pt-0 page-form">
        <div className="modal-header border-0 pb-1 mb-2 d-flex justify-content-center position-relative">
          <h1
            className="modal-title fs-4 fw-bolder text-dark text-center"
            id="dayNotesModalLabel"
          >
            Day Notes Detail
          </h1>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spin size="large" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="modal-body pb-0 pt-2">
            {/* Date and Employee Name Display */}
            <div className="d-flex align-items-center justify-content-between mb-3 text-secondary flex-wrap gap-2">
              <div className="d-flex align-items-center">
                <span className="me-2 d-inline-flex align-items-center text-dark fs-5">
                  {svg?.calenderIcon}
                </span>
                <span className="fs-6 fw-semibold text-dark">
                  Date -{" "}
                  <span className="text-primary fw-medium">
                    {formattedDisplayDate}
                  </span>
                </span>
              </div>
              {showEmployeeName && displayEmployeeName && (
                <div className="d-flex align-items-center">
                  <span className="fs-6 fw-semibold text-dark">
                    <span className="text-primary fw-medium">
                      {displayEmployeeName}
                    </span>
                  </span>
                </div>
              )}
            </div>

            {isPastEndDate && (
              <div
                className="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center"
                role="alert"
                style={{ fontSize: "13px" }}
              >
                <span>
                  Employment ended on{" "}
                  <strong>{formattedEmpEndDate}</strong>.
                  {" "}Day notes for this date are read-only.
                </span>
              </div>
            )}

            {/* Employee Notes */}
            <div className="mb-3">
              <label className="custom-label mb-1 ff fw-bold text-dark fs-6 d-block">
                Employee Notes
              </label>
              <TextArea
                rows={4}
                value={employeeNotes}
                onChange={(e) => setEmployeeNotes(e.target.value)}
                placeholder="Type here ..."
                disabled={!isEmployeeNotesEditable}
                className="form-control form-control-lg"
                style={{
                  resize: "vertical",
                  borderRadius: "8px",
                  backgroundColor: !isEmployeeNotesEditable
                    ? "#f1f3f5"
                    : undefined,
                  cursor: !isEmployeeNotesEditable ? "not-allowed" : "text",
                }}
              />
            </div>

            {/* Manager Notes */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">
                <label className="custom-label mb-0 ff fw-bold text-dark fs-6 d-block">
                  Admin Notes
                </label>
              </div>
              <TextArea
                rows={4}
                value={managerNotes}
                onChange={(e) => setManagerNotes(e.target.value)}
                placeholder="Type here ..."
                disabled={!isAdminNotesEditable}
                className="form-control form-control-lg"
                style={{
                  resize: "vertical",
                  borderRadius: "8px",
                  backgroundColor: !isAdminNotesEditable ? "#f1f3f5" : undefined,
                  cursor: !isAdminNotesEditable ? "not-allowed" : "text",
                }}
              />
              {hasAdminNotes && adminNoteAudit && isAdminOrManager && (
                <span
                  className="text-muted fw-normal"
                  style={{ fontSize: "12px" }}
                >
                  Last updated by{" "}
                  <span className="fw-semibold text-dark">
                    {adminNoteAudit.name}
                  </span>
                  {adminNoteAudit.time ? ` on ${adminNoteAudit.time}` : ""}
                </span>
              )}
            </div>

            {/* Centered Submit / Close Button */}
            <div className="d-flex justify-content-center pt-2 pb-3 gap-2">
              {canSubmit ? (
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary px-5 py-2 fw-semibold"
                  style={{ minWidth: "120px", borderRadius: "8px" }}
                >
                  {submitting ? <BtnLoader /> : "Submit"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  className="btn btn-primary px-5 py-2 fw-semibold"
                  style={{ minWidth: "120px", borderRadius: "8px" }}
                >
                  Close
                </button>
              )}
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default DayNotesDetailModal;
